import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router'
import { cn } from '@/lib/utils'
import { NavigationItem } from './types'

interface AnimatedTabsProps {
  items: NavigationItem[]
}

const transition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.15,
}

const getHoverAnimationProps = (hoveredRect: DOMRect, navRect: DOMRect) => ({
  x: hoveredRect.left - navRect.left - 12,
  y: hoveredRect.top - navRect.top - 8,
  width: hoveredRect.width + 24,
  height: hoveredRect.height + 16,
})

export const AnimatedTabs: FC<AnimatedTabsProps> = ({ items }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [buttonRefs, setButtonRefs] = useState<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    setButtonRefs(prev => prev.slice(0, items.length))
  }, [items.length])

  const navRef = useRef<HTMLDivElement>(null)
  const navRect = navRef.current?.getBoundingClientRect()

  const selectedIndex = useMemo(() => {
    // Find the most specific match
    // Sort tabs by path length (longer paths first) to ensure most specific match
    const sortedItems = [...items].sort((a, b) => {
      const aLength = a.path.length
      const bLength = b.path.length
      return bLength - aLength
    })

    const index = sortedItems.findIndex(item => pathname.startsWith(item.path))

    // If found, convert back to original index
    if (index !== -1) {
      const matchedItem = sortedItems[index]
      return items.findIndex(item => item.path === matchedItem.path)
    }

    return -1
  }, [items, pathname])

  const selectedRect = selectedIndex !== -1 ? buttonRefs[selectedIndex]?.getBoundingClientRect() : undefined

  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null)
  const hoveredRect = buttonRefs[hoveredTabIndex ?? -1]?.getBoundingClientRect()

  return (
    <nav
      ref={navRef}
      className="relative z-0 flex items-center justify-start flex-shrink-0 gap-1 py-3 overflow-x-auto overflow-y-hidden"
    >
      {items.map((item, i) => {
        const isActive = selectedIndex === i
        return (
          <button
            key={item.path}
            className={cn(
              'relative z-20 flex items-center h-8 px-4 text-sm transition-colors bg-transparent cursor-pointer select-none group'
            )}
            onPointerEnter={() => setHoveredTabIndex(i)}
            onPointerLeave={() => setHoveredTabIndex(null)}
            onFocus={() => setHoveredTabIndex(i)}
            onClick={() => navigate(item.path)}
            type="button"
            data-testid={item.title.toLowerCase()}
          >
            <motion.span
              ref={el => {
                buttonRefs[i] = el as HTMLButtonElement
              }}
              className={cn('flex items-center transition-colors duration-300 group-hover:text-primary', {
                'text-muted-foreground': !isActive,
                'text-primary': isActive,
              })}
            >
              {item.title}
            </motion.span>
          </button>
        )
      })}

      {/* Hover animation */}
      <AnimatePresence>
        {hoveredRect && navRect && (
          <motion.div
            key="hover"
            className="absolute top-0 left-0 z-10 p-0 m-0 rounded-md bg-muted"
            initial={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 0 }}
            animate={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 1 }}
            exit={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 0 }}
            transition={transition}
          />
        )}
      </AnimatePresence>

      {/* Selected item indicator */}
      {selectedRect && navRect && selectedIndex !== -1 && (
        <motion.div
          className="absolute rounded-t-full z-10 bottom-0 left-0 h-[2px] bg-primary"
          initial={false}
          animate={{
            width: selectedRect.width + 24,
            x: `calc(${selectedRect.left - navRect.left - 12}px)`,
            opacity: 1,
          }}
          transition={transition}
        />
      )}
    </nav>
  )
}
